
import { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { getAssessmentBySlug } from '@/services/assessmentService';
import { Assessment } from '@/types/assessment';
import FreeAssessment from './FreeAssessment';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/icons/Logo';
import { supabase } from '@/integrations/supabase/client';

const AssessmentPortal = () => {
  const { slug = '', mode = '' } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // For premium mode magic link callbacks, authenticate then redirect to /premium
  useEffect(() => {
    if (mode !== 'premium') return;

    const handlePremiumAuth = async () => {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          const { data: orgMember } = await supabase
            .from('organization_members')
            .select('id')
            .eq('id', session.user.id)
            .maybeSingle();

          if (orgMember) {
            navigate('/full-assessment', { replace: true });
          }
        }
      });

      // Check existing session
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('id')
          .eq('id', session.user.id)
          .maybeSingle();

        if (orgMember) {
          navigate('/full-assessment', { replace: true });
          return;
        }
      }

      // No auth — redirect to login
      navigate('/', { replace: true });

      return () => subscription.unsubscribe();
    };

    handlePremiumAuth();
  }, [mode, navigate]);

  useEffect(() => {
    if (mode === 'premium') return; // Skip fetching for premium mode

    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const data = await getAssessmentBySlug(slug);
        
        if (!data) {
          setError('Assessment not found');
          toast({
            variant: "destructive",
            title: "Error",
            description: "The requested assessment could not be found."
          });
          return;
        }
        
        if (!data.isActive) {
          setError('Assessment is not active');
          toast({
            variant: "destructive",
            title: "Assessment Unavailable",
            description: "This assessment is currently not active."
          });
          return;
        }
        
        setAssessment(data);
        document.title = `Gapstars | ${data.title}`;
      } catch (err) {
        setError('Failed to load assessment');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load the assessment. Please try again later."
        });
        console.error('Error fetching assessment:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [slug, mode, toast]);

  // Premium mode is handled by redirect above
  if (mode === 'premium') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg text-foreground">Redirecting...</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg text-foreground">Loading assessment...</span>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return <Navigate to="/" />;
  }

  if (mode === 'free') {
    return <FreeAssessment assessment={assessment} />;
  }

  return <Navigate to="/" />;
};

export default AssessmentPortal;
